import { useI18n } from "vue-i18n";
import { useColumnSettings } from "@eudonet/opal";
import { buildColumnSettingsLabels } from "@/shared/composables/columnSettingsLabels";
// The "select" (row checkbox) and trailing "actions" columns are intentionally NOT listed here:
// the consuming view (FormListView) prepends/appends them to the order and pins them itself, so
// the settings drawer shows no toggle for them and they never reorder. Listing either here would
// duplicate the id in columnOrder.
// Every data column of the view must appear here, in render order: a key missing from this array
// is pushed behind "actions" by the view's columnOrder and has no visibility toggle.
const DEFAULT_ORDER = [
    "publicSlug",
    "title",
    "campaignId",
    "status",
    "defaultCurrency",
    "donationCount",
    "totalAmountMinor",
    "createdAt",
    "publicUrl",
];
export function useFormListColumnSettings() {
    const { t } = useI18n();
    // A getter rather than computed(): useColumnSettings declares `columns` as
    // MaybeRefOrGetter, so either form is accepted. The getter is reactive all the same —
    // useColumnSettings reads it inside its own computed, which tracks the locale read by
    // t(). (Nothing forces the choice; the brand mismatch this comment used to cite was a
    // duplicate-Vue artefact, gone with the single-Vue `paths` pin in tsconfig.json.)
    const settingsColumns = () => [
        {
            key: "publicSlug",
            label: t("collecte.forms.columns.slug"),
            visible: true,
            pinned: false,
            alwaysVisible: true,
        },
        {
            key: "title",
            label: t("collecte.forms.columns.title"),
            visible: true,
            pinned: false,
        },
        {
            key: "campaignId",
            label: t("collecte.forms.columns.campaign"),
            visible: true,
            pinned: false,
        },
        {
            key: "status",
            label: t("collecte.forms.columns.status"),
            visible: true,
            pinned: false,
        },
        {
            key: "defaultCurrency",
            label: t("collecte.forms.columns.currency"),
            visible: true,
            pinned: false,
        },
        // The two money columns sit between the currency and the audit stamp, so a row reads
        // identity -> state -> money -> when. Their keys are the DonationFormSummary FIELD names
        // (DataTableColumn.key is typed `keyof T`, and OPAL matches settings items to columns by key);
        // the friendly wording lives in the labels, exactly as publicSlug -> columns.slug does.
        {
            key: "donationCount",
            label: t("collecte.forms.columns.donations"),
            visible: true,
            pinned: false,
        },
        {
            key: "totalAmountMinor",
            label: t("collecte.forms.columns.collectedAmount"),
            visible: true,
            pinned: false,
        },
        {
            key: "createdAt",
            label: t("collecte.forms.columns.createdAt"),
            visible: true,
            pinned: false,
        },
        {
            // Derived column (no `publicUrl` field on the row): the view builds it from `publicSlug`
            // plus the runtime public base URL. It is still a DATA column, so it belongs here —
            // otherwise the view's columnOrder pushes it behind the trailing "actions" column and it
            // gets no visibility toggle.
            key: "publicUrl",
            label: t("collecte.forms.columns.publicUrl"),
            visible: true,
            pinned: false,
        },
    ];
    const columnSettings = useColumnSettings({
        columns: settingsColumns,
        defaultOrder: DEFAULT_ORDER,
        // `publicSlug` is alwaysVisible so useColumnSettings already locks it into the left cluster;
        // the consumer (see columnPinning in FormListView) passes this left cluster through untouched
        // and only appends the right-pinned "actions" column on top of it.
        defaultPinning: { left: ["publicSlug"], right: [] },
        // Full shared label set so OPAL never falls back to its English defaults
        // ("Order", "Search a column", "9 of 9 selected", "Pinned"…); only the drawer
        // title is form-list-specific (R69). A getter for consistency with settingsColumns
        // above; `labels` is MaybeRefOrGetter too, so computed() would type-check as well.
        labels: () => buildColumnSettingsLabels(t, { title: t("collecte.forms.columns.customize") }),
    });
    return { columnSettings };
}
